type ProfileScrollIntent = {
  source: string;
  target: string;
  step: string | null;
};

function profileLocation(href: string, base?: string) {
  const url = new URL(href, base);
  const step = url.searchParams.get('initStep');
  url.searchParams.delete('initStep');
  url.searchParams.sort();
  const pathname = url.pathname.replace(/\/+$/, '') || '/';
  return { key: `${url.origin}${pathname}${url.search}${url.hash}`, step };
}

/** Local intent for JobInterview's query-selected profile panels, not browser restoration. */
export function createProfileScrollIntentStore() {
  let intent: ProfileScrollIntent | null = null;
  let reachedTarget = false;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(listener => listener());
  const cancel = () => {
    if (!intent) return;
    intent = null;
    notify();
  };

  return {
    getSnapshot: () => intent,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    request(sourceHref: string, targetHref: string) {
      const source = profileLocation(sourceHref);
      const target = profileLocation(targetHref, sourceHref);
      intent = { source: source.key, target: target.key, step: target.step };
      reachedTarget = source.key === target.key;
      notify();
    },
    observe(href: string) {
      if (!intent) return;
      const location = profileLocation(href);
      if (location.key === intent.target) {
        reachedTarget = true;
      } else if (reachedTarget || location.key !== intent.source) {
        cancel();
      }
    },
    consume(href: string, currentStep: string, expected: ProfileScrollIntent) {
      if (!intent || intent !== expected) return false;
      const location = profileLocation(href);
      if (location.key !== intent.target) return false;
      if (intent.step && intent.step !== currentStep) return false;
      cancel();
      return true;
    },
    cancel,
  };
}

export const profileScrollIntent = createProfileScrollIntentStore();

export function profileScrollBehavior(reducedMotion: boolean): ScrollBehavior {
  // `auto` can still animate when the document has CSS scroll-behavior: smooth.
  return reducedMotion ? 'instant' : 'smooth';
}
